import groupBy from 'lodash-es/groupBy'
import { useContext } from 'react'
import { StringParam, useQueryParams } from 'use-query-params'

import ProfileContext from '@/components/Context/ProfileContext'
import ENUMS from '@/src/enums'

export const modeListDefault = (
  mode?: string | null,
  isAuthenticated?: boolean
): string => (mode ? mode : isAuthenticated ? ENUMS.MODE_LIST : ENUMS.MODE_GRID)

export type AppliedFilter = {
  filter_id: string
  value: string
  __typename?: string
}

export type DisplayOptions = {
  query: string
  queryFacet: string
  resultsPerPage: string
  mode: string
  sortBy: string
  sortOrder: string
  page: number
}

// Encode filtersApplied -> featuresHash (format : id-v1-v2_id2-v3).
// Valeurs laissees brutes : l'encodage URL est gere par use-query-params (StringParam).
// Ne PAS encodeURIComponent ici (sinon double encodage : %20 -> %2520 dans l'URL).
export const encodeFilters = (filters: AppliedFilter[]): string => {
  const grouped = groupBy(filters, f => f.filter_id)
  return Object.keys(grouped)
    .map(filterId => `${filterId}-${grouped[filterId].map(f => f.value).join('-')}`)
    .join('_')
}

// Decode featuresHash -> filtersApplied. La valeur arrive deja decodee par
// use-query-params ; pas de decodeURIComponent (sinon crash sur les valeurs
// contenant un '%' isole, ex: "-50%").
export const decodeFilters = (featuresHash?: string | null): AppliedFilter[] => {
  const segments = featuresHash?.split('_') ?? []
  const filters: AppliedFilter[] = []
  segments.forEach(segment => {
    if (!segment) return
    const parts = segment.split('-')
    const id = parts.shift()
    if (!id) return
    parts.forEach(value => {
      filters.push({
        filter_id: id,
        value,
        __typename: 'FiltersApplied',
      })
    })
  })
  return filters
}

const useSearchState = () => {
  const [urlQueries, setQuery] = useQueryParams({
    query: StringParam,
    queryFacet: StringParam,
    p: StringParam,
    mode: StringParam,
    resultsPerPage: StringParam,
    sortOrder: StringParam,
    sortBy: StringParam,
    featuresHash: StringParam,
    raw_facets: StringParam,
  })

  const { isAuthenticated } = useContext(ProfileContext)

  const displayOptions: DisplayOptions = {
    query: urlQueries.query || '',
    queryFacet: urlQueries.queryFacet || '',
    resultsPerPage: urlQueries.resultsPerPage || ENUMS.RESULT_PER_PAGE_DEFAULT,
    mode: modeListDefault(urlQueries.mode, isAuthenticated),
    sortBy: urlQueries.sortBy || ENUMS.SORTING_BY,
    sortOrder: urlQueries.sortOrder || ENUMS.SORTING_ORDER,
    page: urlQueries.p ? parseInt(urlQueries.p) : 1,
  }

  const filtersApplied = decodeFilters(urlQueries.featuresHash)

  // Patch partiel de displayOptions -> URL
  const setDisplayOptions = (patch: Partial<DisplayOptions>): void => {
    const next = { ...displayOptions, ...patch }

    // Changer le terme recherche (barre principale) ou la sous-recherche dans la
    // liste (queryFacet) change le nombre de resultats : rester sur la page
    // courante peut pointer au-dela de la derniere page et renvoyer une liste
    // vide. On repart de la page 1, sauf si le patch fixe lui-meme la page.
    const changesQuery =
      (patch.query !== undefined && patch.query !== displayOptions.query) ||
      (patch.queryFacet !== undefined && patch.queryFacet !== displayOptions.queryFacet)
    if (changesQuery && patch.page === undefined) {
      next.page = 1
    }

    setQuery({
      query: next.query,
      queryFacet: next.queryFacet,
      p: String(next.page),
      mode: next.mode,
      resultsPerPage: next.resultsPerPage,
      sortOrder: next.sortOrder,
      sortBy: next.sortBy,
    })
  }

  // Remplace la liste de filtres -> URL (reset page a 1 optionnel)
  const setFilters = (filters: AppliedFilter[], resetPage = false): void => {
    setQuery({
      featuresHash: encodeFilters(filters),
      ...(resetPage ? { p: '1' } : {}),
    })
  }

  const resetFilters = (resetPage = true): void => setFilters([], resetPage)

  // Toggle d'une valeur checkbox (ajoute/retire { filter_id, value })
  const setFilterValue = (filterId: string, value: string, checked: boolean): void => {
    const without = filtersApplied.filter(
      f => !(f.filter_id === filterId && f.value === value)
    )
    const next = checked ? [...without, { filter_id: filterId, value }] : without
    setFilters(next, true)
  }

  // Remplace toutes les valeurs d'un filtre (slider numerique) — NE reset PAS la page
  const setNumericFilter = (filterId: string, values: string[]): void => {
    const without = filtersApplied.filter(f => f.filter_id !== filterId)
    const next = [
      ...without,
      ...values.map(value => ({ filter_id: filterId, value, __typename: 'FiltersApplied' })),
    ]
    setFilters(next)
  }

  return {
    displayOptions,
    filtersApplied,
    featuresHash: urlQueries.featuresHash,
    rawFacets: urlQueries.raw_facets,
    setDisplayOptions,
    setFilters,
    resetFilters,
    setFilterValue,
    setNumericFilter,
  }
}

export default useSearchState
